/**
 * @file form - relationFormItems
 * @author sunwen05
 */

import _ from 'lodash';
import React, {useCallback, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {Form} from 'acud';
import {OutlinedDown, OutlinedUp} from 'acud-icon';

import {
    DuFormItemControl,
    OnDisplayItem,
    DISPLAY_ITEM_RELATION,
    RelationFormItemsProps
} from '../../../../hooks/biz/form/interface';
import DynamicFormItem from './DynamicFormItem';
import DuFormItem from './DuFormItem';
import {expressionComparison} from '../formUtil';
import {useLocale} from '../../../locale';

const RelationFormItems: React.FunctionComponent<RelationFormItemsProps> = props => {
    const [locale] = useLocale('HooksForm');

    const {prefixCls, controls, option, formData, form = {}, moreSettingCollapsed} = props || {};

    const colLabel = moreSettingCollapsed ? moreSettingCollapsed.label : undefined;
    const defaultExpand = moreSettingCollapsed ? moreSettingCollapsed.defaultExpand : undefined;
    const expand = moreSettingCollapsed?.expand;
    const setExpand = moreSettingCollapsed?.setExpand;
    const expandIdx = colLabel ? controls.findIndex(item => item.name === colLabel) : 0;

    const [colExpand, setColExpand] = useState(defaultExpand);

    const handleColExpandChange = useCallback(() => {
        setExpand?.(!colExpand);
        setColExpand(!colExpand);
    }, [colExpand, setExpand]);

    // 初始化时调用 TODO !! 考虑nameList
    // 如果配置了高级配置，必须重写validateFileds
    useEffect(() => {
        if (moreSettingCollapsed && form) {
            // eslint-disable-next-line no-console
            console.log('moreSettingCollapsed(高级配置) 改写 validateFields!!');
            const e = form.validateFields;
            const validateFields : any = async (nameList: any, options: any) => {
                try {
                    // 只有校验成功了，才会执行这一步，否则Promise Reject了
                    return await e(nameList, options);
                }
                catch (e: any) {
                    // 如果高级配置里面有问题需要打开
                    e?.errorFields?.every((field: any) => {
                        let {name = []} = field;
                        name = name[0];
                        const index = controls.findIndex(item => item.name === name);
                        if (index > expandIdx) {
                            setColExpand(true);
                            return false;
                        }
                        return true;
                    });
                    return Promise.reject(e);
                }
            };
            form.validateFields = validateFields;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const relationComparison = useCallback((oper: DISPLAY_ITEM_RELATION, rawData: boolean, newData: boolean) => {
        switch (oper) {
            case '&&':
                return rawData && newData;
            default:
                return rawData || newData;
        }
    }, []);

    const getOnDisplay = useCallback((getFieldValue: any, onDisplay: OnDisplayItem[]) => {
        let result = true;
        _.each(onDisplay, (item, i) => {
            const formItemValue = getFieldValue(item.name);
            const value = formItemValue === undefined ? formData[item.name] : formItemValue;
            let itemResult = false;
            _.each(item.content, (values: any, index: number) => {
                const res = expressionComparison(values.exp, values.value, value, values.customMethod);
                itemResult = index === 0 ? res : relationComparison(values.relation, itemResult, res);
            });
            result = i === 0 ? itemResult : relationComparison(item.relation, result, itemResult);
        });
        return result;
    },
    [formData, relationComparison]
    );

    useEffect(() => {
        if (expand) {
            setColExpand?.(expand);
        }
    }, [expand]);

    return (
        <>
            {_.map(controls, (control: DuFormItemControl, index: number) => {
                const isHidden = (moreSettingCollapsed && colLabel && !colExpand && index > expandIdx);
                const itemStyle : React.CSSProperties | undefined
                    = isHidden
                        ? {visibility: 'hidden', height: 0, opacity: 0}
                        : {};
                return (
                    control && (
                        <>
                            <Form.Item
                                shouldUpdate
                                style={itemStyle}
                                key={control.name}
                                noStyle={option.noStyle && !isHidden}
                            >
                                {({getFieldValue}) => {
                                    return !control.onDisplay || getOnDisplay(getFieldValue, control.onDisplay) ? (
                                        control.dynamic ? (
                                            <DynamicFormItem
                                                prefixCls={prefixCls}
                                                key={control.name}
                                                control={control}
                                                option={option}
                                                formData={formData}
                                                form={form}
                                            />
                                        ) : (
                                            <DuFormItem
                                                prefixCls={prefixCls}
                                                key={control.name}
                                                control={control}
                                                option={option}
                                                formData={formData}
                                            />
                                        )
                                    ) : null;
                                }}
                            </Form.Item>
                            {colLabel && control?.name === colLabel && (
                                <div className="more-setting-collapsed" onClick={handleColExpandChange}>
                                    <span className="btn-txt">
                                        {colExpand ? <OutlinedUp width={16} /> : <OutlinedDown width={16} />}
                                        {locale.advanced}
                                    </span>
                                </div>
                            )}
                        </>
                    )
                );
            })}
        </>
    );
};

RelationFormItems.propTypes = {
    controls: PropTypes.array.isRequired,
    option: PropTypes.object.isRequired,
    form: PropTypes.object.isRequired,
    formData: PropTypes.object
};

RelationFormItems.defaultProps = {
    controls: [],
    option: {},
    formData: {},
    form: {}
};

export default RelationFormItems;
